import {
	AlertTriangle,
	Calendar,
	ChevronDown,
	Clock,
	Cloud,
	CloudRain,
	Compass,
	Droplets,
	Eye,
	Gauge,
	Info,
	Loader2,
	MapPin,
	Navigation,
	RefreshCw,
	Sparkles,
	Sun,
	Sunrise,
	Sunset,
	Thermometer,
	Umbrella,
	Wind,
} from "lucide-react";
import {
	type ComponentType,
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	assessAllHours,
	type DailyPeak,
	findDailyPeaks,
	type HourlyAssessment,
	type OpenMeteoResponse,
	scoreBarColor,
	scoreColor,
	scoreLabel,
	wmoDescription,
	wmoIcon,
} from "@/lib/rainbow";
import { compassBearing } from "@/lib/solar";
import { cn } from "@/lib/utils";
import "./index.css";

const WEATHER_API = "https://api.open-meteo.com/v1/forecast";

function buildWeatherUrl(lat: number, lon: number): string {
	const params = new URLSearchParams({
		latitude: lat.toFixed(4),
		longitude: lon.toFixed(4),
		hourly: [
			"temperature_2m",
			"relative_humidity_2m",
			"precipitation",
			"precipitation_probability",
			"cloud_cover",
			"weather_code",
			"wind_speed_10m",
		].join(","),
		daily: [
			"sunrise",
			"sunset",
			"precipitation_probability_max",
			"weather_code",
			"temperature_2m_max",
			"temperature_2m_min",
		].join(","),
		current: [
			"temperature_2m",
			"relative_humidity_2m",
			"precipitation",
			"weather_code",
			"cloud_cover",
			"wind_speed_10m",
		].join(","),
		temperature_unit: "fahrenheit",
		wind_speed_unit: "mph",
		timezone: "auto",
		forecast_days: "7",
		past_days: "1",
	});
	return `${WEATHER_API}?${params}`;
}

type AppStatus =
	| "geo-prompt"
	| "geo-loading"
	| "geo-denied"
	| "geo-insecure"
	| "geo-unavailable"
	| "fetching"
	| "ready"
	| "error";

type IconComponent = ComponentType<{ className?: string }>;
type Accent = "amber" | "cyan" | "emerald" | "rose" | "sky" | "violet";

const accentClasses: Record<Accent, string> = {
	amber: "text-amber-200 bg-amber-300/10 ring-amber-200/15",
	cyan: "text-cyan-200 bg-cyan-300/10 ring-cyan-200/15",
	emerald: "text-emerald-200 bg-emerald-300/10 ring-emerald-200/15",
	rose: "text-rose-200 bg-rose-300/10 ring-rose-200/15",
	sky: "text-sky-200 bg-sky-300/10 ring-sky-200/15",
	violet: "text-violet-200 bg-violet-300/10 ring-violet-200/15",
};

function pad(n: number): string {
	return n.toString().padStart(2, "0");
}

function formatHour(date: Date): string {
	let h = date.getHours();
	const ampm = h >= 12 ? "PM" : "AM";
	h = h % 12 || 12;
	return `${h}${ampm}`;
}

function formatTime(iso: string): string {
	return formatHour(new Date(iso));
}

function currentHourKey(): string {
	const now = new Date();
	return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
		now.getDate(),
	)}T${pad(now.getHours())}:00`;
}

function todayDateStr(): string {
	const now = new Date();
	return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
		now.getDate(),
	)}`;
}

function roundValue(value: number): number {
	return Math.round(value);
}

function isLocationSecureContext(): boolean {
	if (window.isSecureContext) return true;
	return ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
}

function safariLocationHint(): string {
	const ua = navigator.userAgent;
	const isIOS = /iPad|iPhone|iPod/.test(ua);
	const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);

	if (!isSafari) {
		return "Check that this browser can use Location Services, then try again.";
	}

	if (isIOS) {
		return "On iPhone or iPad, open Settings > Privacy & Security > Location Services > Safari Websites, then allow this site.";
	}

	return "On Mac, open Safari Website Settings for this page and set Location to Allow.";
}

function geolocationErrorMessage(error: GeolocationPositionError): string {
	if (error.code === error.PERMISSION_DENIED) {
		return safariLocationHint();
	}

	if (error.code === error.TIMEOUT) {
		return "Location lookup timed out. Keep this tab active and try again with a stronger Wi-Fi or cellular signal.";
	}

	if (error.code === error.POSITION_UNAVAILABLE) {
		return "Your device could not determine a position. Check system Location Services for Safari.";
	}

	return error.message || "Location could not be read right now.";
}

function scoreSummary(score: number): string {
	if (score >= 80) return "Rainbow window open";
	if (score >= 60) return "Step outside soon";
	if (score >= 40) return "Watch the sunbreaks";
	if (score >= 20) return "A brief window is possible";
	return "Low chance right now";
}

function scoreSurface(score: number): string {
	if (score >= 80) return "from-violet-400/25 via-fuchsia-300/10 to-sky-300/15";
	if (score >= 60) return "from-emerald-300/[0.22] via-cyan-300/10 to-violet-300/[0.12]";
	if (score >= 40) return "from-amber-300/20 via-orange-300/10 to-sky-300/10";
	if (score >= 20)
		return "from-orange-300/[0.16] via-rose-300/[0.08] to-sky-300/[0.08]";
	return "from-white/[0.08] via-white/[0.03] to-white/[0.02]";
}

function AppFrame({ children }: { children: ReactNode }) {
	return <div className="app-shell min-h-screen text-white">{children}</div>;
}

function BrandMark({ className }: { className?: string }) {
	return <span className={cn("brand-mark", className)} aria-hidden="true" />;
}

function StateScreen({
	icon: Icon,
	title,
	description,
	children,
	loading = false,
}: {
	icon: IconComponent;
	title: string;
	description: string;
	children?: ReactNode;
	loading?: boolean;
}) {
	return (
		<AppFrame>
			<div className="relative z-10 flex min-h-screen items-center justify-center px-5 py-10 text-center">
				<section className="glass-panel w-full max-w-md rounded-lg p-6 shadow-2xl sm:p-8">
					<BrandMark className="mx-auto mb-6 h-12 w-12" />
					<div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-lg border border-white/10 bg-white/[0.06]">
						<Icon
							className={cn(
								"h-6 w-6 text-white/75",
								loading && "animate-spin text-cyan-200",
							)}
						/>
					</div>
					<h2 className="text-2xl font-semibold tracking-normal text-white">
						{title}
					</h2>
					<p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-white/[0.58]">
						{description}
					</p>
					{children && <div className="mt-6">{children}</div>}
				</section>
			</div>
		</AppFrame>
	);
}

function ScoreRing({ score }: { score: number }) {
	const radius = 82;
	const circumference = 2 * Math.PI * radius;
	const dashOffset = circumference - (score / 100) * circumference;

	return (
		<div
			className="relative mx-auto h-56 w-56 sm:h-64 sm:w-64"
			aria-label={`Rainbow chance ${score} percent`}
		>
			<div className="score-ring-glow" aria-hidden="true" />
			<svg viewBox="0 0 200 200" className="h-full w-full -rotate-90">
				<defs>
					<linearGradient
						id="score-gradient"
						x1="12"
						y1="40"
						x2="184"
						y2="160"
						gradientUnits="userSpaceOnUse"
					>
						<stop offset="0%" stopColor="#fb7185" />
						<stop offset="18%" stopColor="#f59e0b" />
						<stop offset="34%" stopColor="#fde047" />
						<stop offset="52%" stopColor="#34d399" />
						<stop offset="70%" stopColor="#38bdf8" />
						<stop offset="86%" stopColor="#a78bfa" />
						<stop offset="100%" stopColor="#f0abfc" />
					</linearGradient>
				</defs>
				<circle
					cx="100"
					cy="100"
					r={radius}
					fill="none"
					stroke="rgba(255,255,255,0.09)"
					strokeWidth="11"
				/>
				<circle
					cx="100"
					cy="100"
					r={radius}
					fill="none"
					stroke="url(#score-gradient)"
					strokeWidth="11"
					strokeLinecap="round"
					strokeDasharray={circumference}
					strokeDashoffset={dashOffset}
				/>
			</svg>
			<div className="absolute inset-8 flex flex-col items-center justify-center rounded-full border border-white/10 bg-slate-950/[0.45] text-center shadow-inner backdrop-blur-md">
				<div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/[0.42]">
					<Sparkles className="h-3.5 w-3.5" />
					Now
				</div>
				<div className={cn("mt-1 text-6xl font-black tabular-nums", scoreColor(score))}>
					{score}
					<span className="align-super text-2xl">%</span>
				</div>
				<div className="text-sm font-medium text-white/[0.62]">{scoreLabel(score)}</div>
			</div>
		</div>
	);
}

function ConditionPill({
	icon: Icon,
	label,
	value,
	unit,
	accent,
}: {
	icon: IconComponent;
	label: string;
	value: string | number;
	unit?: string;
	accent: Accent;
}) {
	return (
		<div className="condition-tile group min-h-[82px] rounded-lg border border-white/10 bg-white/[0.045] p-3 shadow-sm backdrop-blur-md transition-colors hover:bg-white/[0.07]">
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<div className="text-[11px] font-medium uppercase tracking-[0.14em] text-white/[0.36]">
						{label}
					</div>
					<div className="mt-2 truncate text-xl font-semibold tabular-nums text-white">
						{value}
						{unit && (
							<span className="ml-1 text-sm font-medium text-white/[0.45]">{unit}</span>
						)}
					</div>
				</div>
				<div
					className={cn(
						"flex h-9 w-9 shrink-0 items-center justify-center rounded-md ring-1",
						accentClasses[accent],
					)}
				>
					<Icon className="h-4 w-4" />
				</div>
			</div>
		</div>
	);
}

function CompassArrow({ degrees }: { degrees: number }) {
	return (
		<div className="relative mx-auto h-32 w-32">
			<svg viewBox="0 0 100 100" className="h-full w-full">
				<circle
					cx="50"
					cy="50"
					r="45"
					fill="rgba(255,255,255,0.025)"
					stroke="rgba(255,255,255,0.13)"
					strokeWidth="1.2"
				/>
				<circle
					cx="50"
					cy="50"
					r="31"
					fill="none"
					stroke="rgba(255,255,255,0.08)"
					strokeWidth="1"
				/>
				{[0, 45, 90, 135, 180, 225, 270, 315].map((tick) => (
					<line
						key={tick}
						x1="50"
						y1="6"
						x2="50"
						y2={tick % 90 === 0 ? "13" : "10"}
						stroke={tick % 90 === 0 ? "rgba(255,255,255,0.32)" : "rgba(255,255,255,0.16)"}
						strokeWidth={tick % 90 === 0 ? "1.4" : "1"}
						transform={`rotate(${tick} 50 50)`}
					/>
				))}
				{["N", "E", "S", "W"].map((label, i) => (
					<text
						key={label}
						x="50"
						y="18"
						textAnchor="middle"
						dominantBaseline="central"
						fill="rgba(255,255,255,0.44)"
						fontSize="7"
						fontFamily="system-ui"
						fontWeight="700"
						transform={`rotate(${i * 90} 50 50)`}
					>
						{label}
					</text>
				))}
				<g transform={`rotate(${degrees} 50 50)`}>
					<path
						d="M50 10 L42 45 L50 38 L58 45 Z"
						fill="#f0abfc"
						stroke="rgba(255,255,255,0.38)"
						strokeWidth="0.6"
					/>
					<path d="M50 90 L43 57 L50 63 L57 57 Z" fill="rgba(255,255,255,0.16)" />
				</g>
				<circle cx="50" cy="50" r="4" fill="rgba(255,255,255,0.52)" />
			</svg>
		</div>
	);
}

function DetailRow({
	icon: Icon,
	label,
	value,
	detail,
	accent = "sky",
}: {
	icon: IconComponent;
	label: string;
	value: string;
	detail?: string;
	accent?: Accent;
}) {
	return (
		<div className="flex items-center gap-3 border-b border-white/[0.08] py-3 last:border-b-0">
			<div
				className={cn(
					"flex h-9 w-9 shrink-0 items-center justify-center rounded-md ring-1",
					accentClasses[accent],
				)}
			>
				<Icon className="h-4 w-4" />
			</div>
			<div className="min-w-0 flex-1">
				<div className="text-[11px] font-medium uppercase tracking-[0.14em] text-white/35">
					{label}
				</div>
				<div className="mt-0.5 truncate text-sm font-semibold text-white/[0.88]">{value}</div>
				{detail && <div className="mt-0.5 truncate text-xs text-white/[0.38]">{detail}</div>}
			</div>
		</div>
	);
}

function PeakWindowCard({
	todayPeak,
	bestUpcoming,
}: {
	todayPeak: DailyPeak;
	bestUpcoming: HourlyAssessment | null;
}) {
	const hasPeak = Boolean(todayPeak.peakTime);

	return (
		<Card className="glass-panel min-w-0 border-white/10 bg-white/[0.04] py-0">
			<CardHeader className="px-4 pb-2 pt-4 sm:px-5">
				<div className="flex items-center gap-2">
					<Clock className="h-4 w-4 text-amber-200/80" />
					<CardTitle className="text-sm font-semibold text-white/[0.78]">
						Best Chance Today
					</CardTitle>
				</div>
			</CardHeader>
			<CardContent className="px-4 pb-4 sm:px-5">
				<div className="space-y-4">
					<div className="min-w-0">
						<div className="flex items-baseline gap-2">
							<span
								className={cn(
									"text-4xl font-black tabular-nums",
									scoreColor(todayPeak.peakScore),
								)}
							>
								{todayPeak.peakScore}%
							</span>
							<span className="text-sm font-medium text-white/[0.46]">
								{scoreLabel(todayPeak.peakScore)}
							</span>
						</div>
						<p className="mt-2 text-sm text-white/[0.55]">
							{hasPeak ? (
								<>
									Peak near{" "}
									<span className="font-semibold text-white/[0.86]">
										{formatTime(todayPeak.peakTime)}
									</span>
								</>
							) : (
								"No clear viewing window today"
							)}
						</p>
					</div>
					{bestUpcoming && (
						<div className="rounded-lg border border-white/10 bg-white/[0.035] px-3 py-2">
							<div className="text-[11px] font-medium uppercase tracking-[0.14em] text-white/[0.32]">
								Best in next 24h
							</div>
							<div className="mt-1 text-sm font-semibold text-white/80">
								{formatHour(bestUpcoming.date)} · {bestUpcoming.score}%
							</div>
						</div>
					)}
				</div>
				{todayPeak.sunrise && todayPeak.sunset && (
					<div className="mt-5 grid grid-cols-2 gap-2 text-xs text-white/[0.52]">
						<div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
							<Sunrise className="h-4 w-4 text-amber-200/80" />
							<span>Sunrise {formatTime(todayPeak.sunrise)}</span>
						</div>
						<div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
							<Sunset className="h-4 w-4 text-rose-200/80" />
							<span>Sunset {formatTime(todayPeak.sunset)}</span>
						</div>
					</div>
				)}
			</CardContent>
		</Card>
	);
}

function HourlyTimeline({
	hours,
	maxScore,
}: {
	hours: HourlyAssessment[];
	maxScore: number;
}) {
	return (
		<Card className="glass-panel min-w-0 border-white/10 bg-white/[0.04] py-0">
			<CardHeader className="px-4 pb-2 pt-4 sm:px-5">
				<div className="flex items-center justify-between gap-4">
					<div>
						<div className="flex items-center gap-2">
							<Sun className="h-4 w-4 text-amber-200/80" />
							<CardTitle className="text-sm font-semibold text-white/[0.78]">
								24-Hour Outlook
							</CardTitle>
						</div>
						<CardDescription className="mt-1 text-xs text-white/[0.34]">
							Hourly chance and rain context
						</CardDescription>
					</div>
					<div className="hidden items-center gap-2 text-xs text-white/[0.36] sm:flex">
						<span className="h-2 w-2 rounded-sm bg-emerald-400" />
						Higher chance
					</div>
				</div>
			</CardHeader>
			<CardContent className="px-4 pb-4 sm:px-5">
				<div className="space-y-2 sm:hidden">
					{hours.map((hour, i) => {
						const width = `${Math.max(4, (hour.score / maxScore) * 100)}%`;
						const description = `${formatHour(hour.date)}: ${hour.score}% chance, ${wmoDescription(
							hour.weatherCode,
						)}, ${hour.precipitationProbability}% rain`;

						return (
							<div
								key={hour.time}
								className={cn(
									"grid grid-cols-[3.1rem_1.6rem_minmax(0,1fr)_2.6rem] items-center gap-2 rounded-md border px-2.5 py-2",
									i === 0
										? "border-white/[0.16] bg-white/[0.065]"
										: "border-white/[0.08] bg-white/[0.026]",
								)}
								title={description}
							>
								<div
									className={cn(
										"text-xs font-semibold tabular-nums",
										i === 0 ? "text-white/[0.82]" : "text-white/[0.48]",
									)}
								>
									{formatHour(hour.date)}
								</div>
								<div className="text-base leading-none">{wmoIcon(hour.weatherCode)}</div>
								<div className="min-w-0">
									<div className="h-2 overflow-hidden rounded-full bg-slate-950/[0.45]">
										<div
											className={cn("h-full rounded-full", scoreBarColor(hour.score))}
											style={{ width }}
										/>
									</div>
									<div className="mt-1 truncate text-[10px] text-white/[0.28]">
										{hour.precipitationProbability}% rain · {roundValue(hour.temperature)}°F
									</div>
								</div>
								<div className={cn("text-right text-xs font-bold tabular-nums", scoreColor(hour.score))}>
									{hour.score}%
								</div>
							</div>
						);
					})}
				</div>

				<div className="hidden gap-2 pb-1 pt-2 sm:grid sm:grid-cols-8 lg:grid-cols-12">
					{hours.map((hour, i) => {
						const height = `${Math.max(7, (hour.score / maxScore) * 100)}%`;
						const description = `${formatHour(hour.date)}: ${hour.score}% chance, ${wmoDescription(
							hour.weatherCode,
						)}, ${hour.precipitationProbability}% rain`;

						return (
							<div
								key={hour.time}
								className="group flex min-w-0 flex-col items-center gap-2"
								title={description}
							>
								<div className="relative flex h-20 w-full max-w-9 items-end overflow-hidden rounded-md border border-white/[0.08] bg-slate-950/[0.36] sm:h-24 lg:h-28">
									<div className="absolute inset-x-0 top-0 h-px bg-white/10" />
									<div
										className={cn(
											"w-full rounded-t-md transition-all group-hover:brightness-125",
											scoreBarColor(hour.score),
										)}
										style={{ height }}
									/>
								</div>
								<div className="flex min-h-[30px] flex-col items-center justify-start sm:min-h-[34px]">
									<span
										className={cn(
											"text-[9px] font-medium tabular-nums sm:text-[10px]",
											i === 0 ? "text-white/[0.78]" : "text-white/[0.34]",
										)}
									>
										{formatHour(hour.date)}
									</span>
									<span className="text-[9px] leading-none text-white/[0.22] sm:text-[10px]">
										{hour.score}%
									</span>
								</div>
							</div>
						);
					})}
				</div>
			</CardContent>
		</Card>
	);
}

function DailyOutlook({ days }: { days: DailyPeak[] }) {
	return (
		<Card className="glass-panel min-w-0 border-white/10 bg-white/[0.04] py-0">
			<CardHeader className="px-4 pb-2 pt-4 sm:px-5">
				<div className="flex items-center gap-2">
					<Calendar className="h-4 w-4 text-violet-200/80" />
					<CardTitle className="text-sm font-semibold text-white/[0.78]">
						Week Ahead
					</CardTitle>
				</div>
			</CardHeader>
			<CardContent className="px-4 pb-4 sm:px-5">
				<div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
					{days.map((day, i) => (
						<div
							key={day.date}
							className={cn(
								"min-h-[118px] rounded-lg border p-3 text-center transition-colors sm:min-h-[132px]",
								i === 0
									? "border-white/[0.16] bg-white/[0.075]"
									: "border-white/[0.08] bg-white/[0.03] hover:bg-white/[0.055]",
							)}
						>
							<div className="text-xs font-semibold uppercase tracking-[0.12em] text-white/[0.45]">
								{i === 0 ? "Today" : day.dayName}
							</div>
							<div className="mt-2 text-2xl leading-none">{wmoIcon(day.weatherCode)}</div>
							<div className={cn("mt-3 text-xl font-black tabular-nums", scoreColor(day.peakScore))}>
								{day.peakScore}%
							</div>
							<div className="mt-1 min-h-4 text-[11px] font-medium text-white/40">
								{day.peakTime ? formatTime(day.peakTime) : "None"}
							</div>
							<div className="mt-2 flex items-center justify-center gap-1 text-[11px] text-white/[0.28]">
								<Umbrella className="h-3 w-3" />
								{day.precipProbMax}%
							</div>
							<div className="mt-1 text-[11px] text-white/[0.24]">
								{roundValue(day.tempMax)}° / {roundValue(day.tempMin)}°
							</div>
						</div>
					))}
				</div>
			</CardContent>
		</Card>
	);
}

export function App() {
	const [status, setStatus] = useState<AppStatus>("geo-prompt");
	const [coords, setCoords] = useState<{ lat: number; lon: number } | null>(
		null,
	);
	const [weather, setWeather] = useState<OpenMeteoResponse | null>(null);
	const [errorMsg, setErrorMsg] = useState("");
	const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
	const [showInfo, setShowInfo] = useState(false);
	const [isRefreshing, setIsRefreshing] = useState(false);

	const fetchWeather = useCallback(
		async (lat: number, lon: number, background = false) => {
			setErrorMsg("");
			if (background) {
				setIsRefreshing(true);
			} else {
				setStatus("fetching");
			}

			try {
				const res = await fetch(buildWeatherUrl(lat, lon));
				if (!res.ok) throw new Error(`Weather API returned ${res.status}`);
				const data: OpenMeteoResponse = await res.json();
				setWeather(data);
				setLastUpdated(new Date());
				setStatus("ready");
			} catch (err) {
				setErrorMsg(
					err instanceof Error ? err.message : "Forecast request failed",
				);
				setStatus("error");
			} finally {
				setIsRefreshing(false);
			}
		},
		[],
	);

	const requestLocation = useCallback(() => {
		setStatus("geo-loading");
		setErrorMsg("");

		if (!isLocationSecureContext()) {
			setStatus("geo-insecure");
			setErrorMsg(
				"Safari only shares location over HTTPS or localhost. Open a secure URL, then try again.",
			);
			return;
		}

		if (!navigator.geolocation) {
			setStatus("geo-unavailable");
			setErrorMsg("This browser does not support location access.");
			return;
		}

		navigator.geolocation.getCurrentPosition(
			(pos) => {
				const c = { lat: pos.coords.latitude, lon: pos.coords.longitude };
				setCoords(c);
				fetchWeather(c.lat, c.lon);
			},
			(err) => {
				setErrorMsg(geolocationErrorMessage(err));
				if (err.code === err.PERMISSION_DENIED) {
					setStatus("geo-denied");
				} else {
					setStatus("geo-unavailable");
				}
			},
			{ enableHighAccuracy: false, timeout: 30000, maximumAge: 600000 },
		);
	}, [fetchWeather]);

	const assessments = useMemo(() => {
		if (!weather || !coords) return [];
		return assessAllHours(weather, coords.lat, coords.lon);
	}, [weather, coords]);

	const dailyPeaks = useMemo(() => {
		if (!weather || assessments.length === 0) return [];
		return findDailyPeaks(weather, assessments);
	}, [weather, assessments]);

	const currentAssessment = useMemo(() => {
		const key = currentHourKey();
		return (
			assessments.find((a) => a.time === key) ??
			assessments[assessments.length > 24 ? 24 : 0] ??
			null
		);
	}, [assessments]);

	const todayStr = useMemo(() => todayDateStr(), [status]);

	const todayPeak = useMemo(() => {
		if (dailyPeaks.length === 0) return null;
		return dailyPeaks.find((d) => d.date === todayStr) ?? dailyPeaks[0] ?? null;
	}, [dailyPeaks, todayStr]);

	const upcomingHours = useMemo(() => {
		const key = currentHourKey();
		const idx = assessments.findIndex((a) => a.time === key);
		const start = idx >= 0 ? idx : assessments.length > 24 ? 24 : 0;
		return assessments.slice(start, start + 24);
	}, [assessments]);

	const bestUpcoming = useMemo(
		() =>
			upcomingHours.reduce<HourlyAssessment | null>(
				(best, hour) => (!best || hour.score > best.score ? hour : best),
				null,
			),
		[upcomingHours],
	);

	const maxUpcomingScore = useMemo(
		() => Math.max(10, ...upcomingHours.map((h) => h.score)),
		[upcomingHours],
	);

	const handleRefresh = () => {
		if (coords) {
			fetchWeather(coords.lat, coords.lon, status === "ready");
		} else {
			requestLocation();
		}
	};

	useEffect(() => {
		if (status === "ready") {
			window.scrollTo({ top: 0, left: 0, behavior: "auto" });
		}
	}, [status]);

	if (status === "geo-prompt") {
		return (
			<StateScreen
				icon={Compass}
				title="Use Your Current Location"
				description="Tap once to align nearby showers, sun angle, and the direction to look."
			>
				<Button
					onClick={requestLocation}
					variant="outline"
					className="border-white/15 bg-white/10 text-white hover:bg-white/15 hover:text-white"
				>
					<MapPin className="h-4 w-4" />
					Use Current Location
				</Button>
			</StateScreen>
		);
	}

	if (status === "geo-loading") {
		return (
			<StateScreen
				icon={Loader2}
				title="Locating Sky Conditions"
				description="Keep this tab active while your device shares its position."
				loading
			/>
		);
	}

	if (status === "geo-denied") {
		return (
			<StateScreen
				icon={MapPin}
				title="Location Permission Needed"
				description={errorMsg || safariLocationHint()}
			>
				<Button
					onClick={requestLocation}
					variant="outline"
					className="border-white/15 bg-white/10 text-white hover:bg-white/15 hover:text-white"
				>
					<MapPin className="h-4 w-4" />
					Try Location Again
				</Button>
			</StateScreen>
		);
	}

	if (status === "geo-insecure") {
		return (
			<StateScreen
				icon={AlertTriangle}
				title="Open a Secure Page"
				description={
					errorMsg ||
					"Open this app with HTTPS or from http://localhost. Safari will not share location with a regular HTTP network address."
				}
			>
				<Button
					onClick={requestLocation}
					variant="outline"
					className="border-white/15 bg-white/10 text-white hover:bg-white/15 hover:text-white"
				>
					<RefreshCw className="h-4 w-4" />
					Check Location Access
				</Button>
			</StateScreen>
		);
	}

	if (status === "geo-unavailable") {
		return (
			<StateScreen
				icon={AlertTriangle}
				title="Location Not Available"
				description={
					errorMsg || "This browser or device could not provide a position."
				}
			>
				<Button
					onClick={requestLocation}
					variant="outline"
					className="border-white/15 bg-white/10 text-white hover:bg-white/15 hover:text-white"
				>
					<RefreshCw className="h-4 w-4" />
					Try Again
				</Button>
			</StateScreen>
		);
	}

	if (status === "fetching") {
		return (
			<StateScreen
				icon={Loader2}
				title="Loading Rainbow Forecast"
				description="Checking showers, cloud breaks, humidity, and low-sun windows."
				loading
			/>
		);
	}

	if (status === "error") {
		return (
			<StateScreen
				icon={AlertTriangle}
				title="Forecast Unavailable"
				description={errorMsg || "Forecast data could not be loaded right now."}
			>
				<Button
					onClick={handleRefresh}
					variant="outline"
					className="border-white/15 bg-white/10 text-white hover:bg-white/15 hover:text-white"
				>
					<RefreshCw className="h-4 w-4" />
					Retry
				</Button>
			</StateScreen>
		);
	}

	const cur = weather!.current;
	const curAssess = currentAssessment?.assessment;
	const curScore = currentAssessment?.score ?? 0;
	const viewDir = curAssess?.viewDirection;
	const tzAbbr = weather!.timezone_abbreviation;
	const currentWeather = `${wmoIcon(cur.weather_code)} ${wmoDescription(
		cur.weather_code,
	)}`;

	return (
		<AppFrame>
			<header className="sticky top-0 z-30 border-b border-white/10 bg-[#071019]/[0.78] backdrop-blur-xl">
				<div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
					<div className="flex min-w-0 items-center gap-3">
						<BrandMark className="h-10 w-10 shrink-0" />
						<div className="min-w-0">
							<h2 className="truncate text-base font-semibold tracking-normal text-white">
								Hawaii Rainbow Tracker
							</h2>
							<div className="mt-0.5 flex items-center gap-1.5 text-xs text-white/[0.42]">
								<MapPin className="h-3.5 w-3.5 shrink-0" />
								<span className="truncate">
									{coords!.lat.toFixed(2)}°, {coords!.lon.toFixed(2)}°
									{tzAbbr ? ` · ${tzAbbr}` : ""}
								</span>
							</div>
						</div>
					</div>
					<div className="flex shrink-0 items-center gap-2">
						{lastUpdated && (
							<span className="hidden text-xs text-white/[0.34] sm:block">
								Updated{" "}
								{lastUpdated.toLocaleTimeString([], {
									hour: "numeric",
									minute: "2-digit",
								})}
							</span>
						)}
						<Button
							variant="ghost"
							size="icon-sm"
							onClick={handleRefresh}
							disabled={isRefreshing}
							className="text-white/[0.56] hover:bg-white/10 hover:text-white"
							aria-label="Refresh forecast"
						>
							<RefreshCw
								className={cn("h-4 w-4", isRefreshing && "animate-spin")}
							/>
						</Button>
					</div>
				</div>
			</header>

			<section
				aria-label="Live rainbow forecast"
				className="relative z-10 mx-auto max-w-6xl space-y-4 overflow-hidden px-4 py-5 pb-16 sm:px-6 lg:px-8"
			>
				<Card
					className={cn(
						"glass-panel overflow-hidden border-white/10 bg-gradient-to-br py-0 shadow-2xl",
						scoreSurface(curScore),
					)}
				>
					<CardContent className="p-0">
						<div className="grid gap-0 lg:grid-cols-[minmax(0,1fr)_360px]">
							<section className="relative min-h-[420px] px-5 py-6 sm:px-7 lg:px-8">
								<div className="rainbow-rule mb-6" aria-hidden="true" />
								<div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_260px] lg:items-center">
									<div className="min-w-0">
										<div className="flex items-center gap-2 text-sm font-medium text-white/[0.55]">
											<CloudRain className="h-4 w-4 text-cyan-100/70" />
											<span>{currentWeather}</span>
											<span className="text-white/25">·</span>
											<span>{roundValue(cur.temperature_2m)}°F</span>
										</div>
										<h2 className="mt-4 max-w-xl text-4xl font-black tracking-normal text-white sm:text-5xl">
											{scoreSummary(curScore)}
										</h2>
										<p className="mt-4 max-w-lg text-base leading-7 text-white/[0.58]">
											{curScore >= 20 && viewDir
												? `Look ${viewDir.bearing} when the sun breaks through. Keep the sun behind you toward ${compassBearing(
														curAssess?.sunAzimuth ?? 0,
													)}.`
												: "Conditions are quiet right now. Check the upcoming windows for a better mix of light and showers."}
										</p>
									</div>
									<ScoreRing score={curScore} />
								</div>
							</section>

							<aside className="border-t border-white/10 bg-slate-950/[0.24] px-5 py-5 backdrop-blur-md lg:border-l lg:border-t-0">
								<div className="mb-4 flex items-center justify-between gap-3">
									<div>
										<div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/[0.36]">
											Where to Look
										</div>
										<div className="mt-1 text-lg font-semibold text-white">
											{viewDir && curScore >= 20
												? `Look ${viewDir.bearing}`
												: "No direction yet"}
										</div>
									</div>
									<div className="flex h-11 w-11 items-center justify-center rounded-lg border border-white/10 bg-white/[0.055]">
										<Navigation className="h-5 w-5 text-violet-100/80" />
									</div>
								</div>

								{viewDir && curScore >= 20 ? (
									<>
										<CompassArrow degrees={viewDir.degrees} />
										<div className="mt-3 text-center text-xs leading-5 text-white/[0.42]">
											Sun altitude {curAssess?.sunAltitude.toFixed(1)}° · anti-solar
											direction {viewDir.degrees.toFixed(0)}°
										</div>
									</>
								) : (
									<div className="flex min-h-[158px] items-center justify-center rounded-lg border border-dashed border-white/[0.12] bg-white/[0.025] p-4 text-center text-sm leading-6 text-white/[0.42]">
										A look direction appears when sunlight and showers are both in
										range.
									</div>
								)}

								<div className="mt-5">
									<DetailRow
										icon={Gauge}
										label="Chance"
										value={`${scoreLabel(curScore)} · ${curScore}%`}
										detail="Based on sun angle, rain, cloud breaks, and humidity"
										accent="violet"
									/>
									<DetailRow
										icon={Eye}
										label="Sun Angle"
										value={
											curAssess
												? `${curAssess.sunAltitude.toFixed(1)}° altitude`
												: "Not available"
										}
										detail={
											curAssess
												? `Sun bearing ${compassBearing(curAssess.sunAzimuth)}`
												: undefined
										}
										accent="amber"
									/>
									<DetailRow
										icon={Clock}
										label="Next Peak"
										value={
											bestUpcoming
												? `${formatHour(bestUpcoming.date)} · ${bestUpcoming.score}%`
												: "Not available"
										}
										detail={
											bestUpcoming
												? `${bestUpcoming.precipitationProbability}% rain chance`
												: undefined
										}
										accent="emerald"
									/>
								</div>
							</aside>
						</div>
					</CardContent>
				</Card>

				<section className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
					<ConditionPill
						icon={Thermometer}
						label="Temperature"
						value={roundValue(cur.temperature_2m)}
						unit="°F"
						accent="rose"
					/>
					<ConditionPill
						icon={Droplets}
						label="Humidity"
						value={cur.relative_humidity_2m}
						unit="%"
						accent="cyan"
					/>
					<ConditionPill
						icon={Cloud}
						label="Cloud Cover"
						value={cur.cloud_cover}
						unit="%"
						accent="sky"
					/>
					<ConditionPill
						icon={Wind}
						label="Wind"
						value={roundValue(cur.wind_speed_10m)}
						unit="mph"
						accent="emerald"
					/>
					<ConditionPill
						icon={CloudRain}
						label="Observed Rain"
						value={cur.precipitation}
						unit="mm"
						accent="violet"
					/>
				</section>

				<section className="grid min-w-0 gap-4 lg:grid-cols-[0.9fr_1.3fr]">
					{todayPeak && (
						<PeakWindowCard todayPeak={todayPeak} bestUpcoming={bestUpcoming} />
					)}
					{upcomingHours.length > 0 && (
						<HourlyTimeline hours={upcomingHours} maxScore={maxUpcomingScore} />
					)}
				</section>

				{dailyPeaks.length > 0 && <DailyOutlook days={dailyPeaks} />}

				<Card className="glass-panel border-white/10 bg-white/[0.035] py-0">
					<CardHeader className="px-4 py-0 sm:px-5">
						<button
							type="button"
							className="flex w-full items-center justify-between gap-4 py-4 text-left"
							onClick={() => setShowInfo((v) => !v)}
							aria-expanded={showInfo}
						>
							<span className="flex items-center gap-2">
								<Info className="h-4 w-4 text-sky-200/75" />
								<CardTitle className="text-sm font-semibold text-white/[0.74]">
									Scoring
								</CardTitle>
							</span>
							<ChevronDown
								className={cn(
									"h-4 w-4 text-white/[0.34] transition-transform",
									showInfo && "rotate-180",
								)}
							/>
						</button>
					</CardHeader>
					{showInfo && (
						<CardContent className="space-y-3 px-4 pb-5 text-sm leading-6 text-white/[0.52] sm:px-5">
							<p>
								<strong className="text-white/[0.72]">Rainbow geometry:</strong> the
								primary arc appears opposite the sun, so the app turns solar
								azimuth into a practical viewing bearing.
							</p>
							<p>
								<strong className="text-white/[0.72]">Score inputs:</strong> low sun,
								nearby rain, broken cloud cover, and humidity. Hawaii's
								trade-wind showers can create short viewing windows while nearby
								sunlight stays strong.
							</p>
							<p className="border-t border-white/[0.08] pt-3 text-xs text-white/[0.34]">
								Weather data from{" "}
								<a
									href="https://open-meteo.com/"
									target="_blank"
									rel="noopener noreferrer"
									className="underline decoration-white/20 underline-offset-4 hover:text-white/60"
								>
									Open-Meteo
								</a>
								. Scores are guidance, not guarantees.
							</p>
						</CardContent>
					)}
				</Card>
			</section>
		</AppFrame>
	);
}

export default App;
